import { useState, useEffect, useCallback, useMemo } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import {
  Container,
  Card,
  Select,
  InputDiv,
  RadioInputDiv,
  RadioWrapper,
  ActionButtonsRow,
  PageContainer,
  PageHeader,
  InfoCard,
  Badge,
  BackButton,
  Result,
  MathDiv,
  CopyHTMLButton,
  AdvancedOptionsButton,
} from '../../../styles/global'
import { AddButton, TableContainer, Table, Tr, Td, Th, SegmentBox, StatusTag, Hint } from './styles'
import { getAvailableMethods, getArrangements, ARRANGEMENT_LABELS } from '../Circuits/conductorConfig'
import { FaPlusCircle, FaMinusCircle } from 'react-icons/fa'
import { FiArrowLeft, FiArrowUp, FiArrowDown, FiInfo } from 'react-icons/fi'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'

const HISTORY_KEY = 'electrical-voltage-drop-history'
const MAX_HISTORY = 10
const MAX_SEGMENTS = 100

const MODES = [
  { value: 'size-cable', label: 'Dimensionar cabo' },
  { value: 'calculate-drop', label: 'Calcular queda' },
]

const SUPPLY_TYPES = [
  { value: 'single-phase', label: 'Monofásico (fase-neutro)' },
  { value: 'two-phase', label: 'Bifásico (fase-fase)' },
  { value: 'three-phase', label: 'Trifásico equilibrado' },
]

const REASONS = {
  'ampacity-exceeded': 'Nenhuma seção comercial atende à capacidade de corrente.',
  'reactance-limit-exceeded': 'A reatância sozinha já excede o limite de queda do trecho.',
  'voltage-drop-exceeded': 'Nenhuma seção comercial atende ao limite de queda de tensão.',
}

// Os rótulos são mais longos que a largura padrão (160px) do RadioWrapper e ficariam cortados.
const radioStyle = { flex: '0 0 auto' }

const emptyAdvanced = {
  insulation: '',
  method: '',
  arrangement: '',
  temperature: '',
  reactanceOhmPerKm: '',
  temperatureFactor: '',
  groupFactor: '',
}

function createSegment(key) {
  return {
    key,
    name: '',
    length: '',
    inputType: 'current',
    load: '',
    powerFactor: '',
    section: '',
    maxDropPercent: '',
    minSection: '',
  }
}

const initialForm = {
  mode: 'size-cable',
  selectedSystem: '127V/220V',
  supplyType: 'single-phase',
  initialDropPercent: '0',
  maxTotalDropPercent: '3',
  segments: [createSegment(1)],
  advanced: emptyAdvanced,
}

function isEmpty(value) {
  return value === '' || value === null || value === undefined
}

function fmt(value, digits = 3) {
  if (value === null || value === undefined) return '—'

  return Number(value).toLocaleString('pt-BR', { maximumFractionDigits: digits })
}

function tex(value, digits = 3) {
  return fmt(value, digits).replace(/\s/g, '\\,')
}

function checkNumber(value, { min, max, minExclusive = false, required = false }) {
  if (isEmpty(value)) return required ? 'Campo obrigatório' : null

  const n = Number(value)

  if (Number.isNaN(n)) return 'Valor deve ser um número'
  if (minExclusive ? n <= min : n < min) return minExclusive ? `Valor deve ser maior que ${min}` : `Mínimo: ${min}`
  if (max !== undefined && n > max) return `Máximo: ${max}`

  return null
}

function validateForm(form) {
  const errors = {}

  errors.initialDropPercent = checkNumber(form.initialDropPercent, { min: 0, max: 100 })
  errors.maxTotalDropPercent = checkNumber(form.maxTotalDropPercent, { min: 0, max: 100, minExclusive: true })

  form.segments.forEach((segment, index) => {
    const p = `segment-${index}-`

    errors[`${p}length`] = checkNumber(segment.length, { min: 0, required: true })
    errors[`${p}load`] = checkNumber(segment.load, { min: 0, required: true })
    errors[`${p}powerFactor`] = checkNumber(segment.powerFactor, { min: 0, max: 1, minExclusive: true })
    errors[`${p}maxDropPercent`] = checkNumber(segment.maxDropPercent, { min: 0, max: 100, minExclusive: true })

    if (form.mode === 'size-cable') {
      errors[`${p}minSection`] = checkNumber(segment.minSection, { min: 0.5, max: 1000 })
    } else if (isEmpty(segment.section)) {
      errors[`${p}section`] = 'Selecione a seção'
    }
  })

  const adv = form.advanced

  errors.temperature = checkNumber(adv.temperature, { min: 20 })
  errors.reactanceOhmPerKm = checkNumber(adv.reactanceOhmPerKm, { min: 0 })
  errors.temperatureFactor = checkNumber(adv.temperatureFactor, { min: 0, max: 1, minExclusive: true })
  errors.groupFactor = checkNumber(adv.groupFactor, { min: 0, max: 1, minExclusive: true })

  return errors
}

export function ElectricalVoltageDrop() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [options, setOptions] = useState(null)
  const [form, setForm] = useState(initialForm)
  const [nextKey, setNextKey] = useState(2)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [touched, setTouched] = useState({})
  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const errors = useMemo(() => validateForm(form), [form])
  const isFormValid = !Object.values(errors).some(Boolean)

  const columnCount = options?.loadedConductors?.[form.supplyType] ?? (form.supplyType === 'three-phase' ? 3 : 2)
  const defaultConductor = options?.defaults?.conductor || { insulation: 'PVC', method: 'A2' }
  const effectiveMethod = form.advanced.method || defaultConductor.method
  const methods = useMemo(() => getAvailableMethods(options?.conductorOptions, columnCount), [options, columnCount])
  const arrangements = useMemo(
    () => getArrangements(options?.conductorOptions, effectiveMethod, columnCount),
    [options, effectiveMethod, columnCount]
  )

  useEffect(() => {
    async function fetchOptions() {
      try {
        const response = await api.get('/electrical/voltage-drop/data')
        setOptions(response.data)
        setForm((prev) => ({
          ...prev,
          maxTotalDropPercent: String(response.data.defaults?.maxTotalDropPercent ?? prev.maxTotalDropPercent),
        }))
      } catch (error) {
        console.error('Erro ao carregar opções de queda de tensão:', error)
        toast.error('Não foi possível carregar as opções da calculadora.')
      }
    }

    fetchOptions()
  }, [])

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)

    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory))
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  function updateForm(changes) {
    setForm((prev) => ({ ...prev, ...changes }))
  }

  function updateAdvanced(changes) {
    setForm((prev) => ({ ...prev, advanced: { ...prev.advanced, ...changes } }))
  }

  function updateSegment(index, changes) {
    setForm((prev) => ({
      ...prev,
      segments: prev.segments.map((segment, i) => (i === index ? { ...segment, ...changes } : segment)),
    }))
  }

  function addSegment(event) {
    event.preventDefault()

    if (form.segments.length >= MAX_SEGMENTS) return

    setForm((prev) => ({ ...prev, segments: [...prev.segments, createSegment(nextKey)] }))
    setNextKey((prev) => prev + 1)
  }

  function removeSegment(event, index) {
    event.preventDefault()

    setForm((prev) =>
      prev.segments.length > 1 ? { ...prev, segments: prev.segments.filter((_, i) => i !== index) } : prev
    )
    setTouched({})
  }

  function moveSegment(event, index, direction) {
    event.preventDefault()

    setForm((prev) => {
      const target = index + direction

      if (target < 0 || target >= prev.segments.length) return prev

      const segments = [...prev.segments]
      ;[segments[index], segments[target]] = [segments[target], segments[index]]

      return { ...prev, segments }
    })
    setTouched({})
  }

  function changeSupplyType(value) {
    // A coluna de condutores carregados muda com a alimentação: descarta método/disposição possivelmente inválidos.
    updateForm({ supplyType: value, advanced: { ...form.advanced, method: '', arrangement: '' } })
  }

  function changeMethod(value) {
    updateAdvanced({ method: value, arrangement: '' })
  }

  // Envia apenas os campos aplicáveis ao modo e à entrada escolhida; vazio significa "usar o padrão da API".
  function buildPayload() {
    const adv = form.advanced
    const hasConductor = adv.insulation !== '' || adv.method !== ''

    const conductor = hasConductor
      ? {
          material: 'copper',
          insulation: adv.insulation || defaultConductor.insulation,
          method: effectiveMethod,
          ...(arrangements.length > 0 ? { arrangement: adv.arrangement || arrangements[0] } : {}),
        }
      : null

    const segments = form.segments.map((segment) => {
      const item = { length: Number(segment.length) }

      if (segment.name.trim() !== '') item.name = segment.name.trim()
      if (segment.inputType === 'current') item.current = Number(segment.load)
      else item.activePower = Number(segment.load)
      if (!isEmpty(segment.powerFactor)) item.powerFactor = Number(segment.powerFactor)
      if (!isEmpty(segment.maxDropPercent)) item.maxDropPercent = Number(segment.maxDropPercent)

      if (form.mode === 'calculate-drop') item.section = Number(segment.section)
      else if (!isEmpty(segment.minSection)) item.minSection = Number(segment.minSection)

      if (conductor) item.conductor = conductor
      ;['temperature', 'reactanceOhmPerKm', 'temperatureFactor', 'groupFactor'].forEach((field) => {
        if (!isEmpty(adv[field])) item[field] = Number(adv[field])
      })

      return item
    })

    const payload = {
      mode: form.mode,
      selectedSystem: form.selectedSystem,
      supplyType: form.supplyType,
      segments,
    }

    if (!isEmpty(form.initialDropPercent)) payload.initialDropPercent = Number(form.initialDropPercent)
    if (!isEmpty(form.maxTotalDropPercent)) payload.maxTotalDropPercent = Number(form.maxTotalDropPercent)

    return payload
  }

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = { ...calculationData, timestamp: new Date().toISOString(), id: Date.now() }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  // Restaura o estado do formulário garantindo valores padrão para campos ausentes em entradas antigas.
  function restoreState(state) {
    const saved = state.form?.segments
    const segments = Array.isArray(saved) && saved.length > 0 ? saved : [createSegment(1)]
    const restored = segments.map((segment, index) => ({ ...createSegment(index + 1), ...segment, key: index + 1 }))

    setForm({
      ...initialForm,
      ...state.form,
      segments: restored,
      advanced: { ...emptyAdvanced, ...state.form?.advanced },
    })
    setNextKey(restored.length + 1)
    setResult(state.result ?? '')
    setData(state.data ?? {})
    setTouched({})
  }

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]

      restoreState(entry)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction

      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const payload = buildPayload()

      setData(payload)

      const response = await api.post('/electrical/voltage-drop', payload)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({ form, result: response.data, data: payload })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      if (error.response?.status === 400 && error.response?.data?.message) {
        toast.error(error.response.data.message)
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = { form, result, data }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Queda de Tensão.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]

    if (!file) return

    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        restoreState(jsonData)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }

    reader.readAsText(file)
    event.target.value = ''
  }

  function handleCopyResults() {
    copyResultsToClipboard({ result })
  }

  function renderSegmentFields(segment, index) {
    const p = `segment-${index}-`
    const isCurrent = segment.inputType === 'current'

    return (
      <SegmentBox key={segment.key}>
        <div className="segment-header">
          <strong>Trecho {index + 1}</strong>
          <div className="segment-actions">
            <button
              type="button"
              title="Mover para cima"
              disabled={index === 0 || isLoading}
              onClick={(e) => moveSegment(e, index, -1)}
            >
              <FiArrowUp />
            </button>
            <button
              type="button"
              title="Mover para baixo"
              disabled={index === form.segments.length - 1 || isLoading}
              onClick={(e) => moveSegment(e, index, 1)}
            >
              <FiArrowDown />
            </button>
            <button
              type="button"
              title="Remover trecho"
              disabled={form.segments.length < 2 || isLoading}
              onClick={(e) => removeSegment(e, index)}
            >
              <FaMinusCircle />
            </button>
          </div>
        </div>
        <InputDiv>
          <span>Nome (opcional)</span>
          <input
            type="text"
            maxLength={120}
            value={segment.name}
            placeholder={`Trecho ${index + 1}`}
            onChange={(event) => updateSegment(index, { name: event.target.value })}
            disabled={isLoading}
          />
        </InputDiv>
        <InputDiv>
          <span>Comprimento de ida (m)</span>
          <ValidatedInput
            name={`${p}length`}
            type="number"
            value={segment.length}
            onChange={(event) => updateSegment(index, { length: event.target.value })}
            errors={errors}
            touched={touched}
            onBlur={handleBlur}
            disabled={isLoading}
          />
        </InputDiv>
        <RadioInputDiv>
          <span>Entrada da carga</span>
          <div>
            <RadioWrapper style={radioStyle}>
              <input
                type="radio"
                name={`${p}inputType`}
                checked={isCurrent}
                onChange={() => updateSegment(index, { inputType: 'current' })}
                disabled={isLoading}
              />
              <span>Corrente (A)</span>
            </RadioWrapper>
            <RadioWrapper style={radioStyle}>
              <input
                type="radio"
                name={`${p}inputType`}
                checked={!isCurrent}
                onChange={() => updateSegment(index, { inputType: 'power' })}
                disabled={isLoading}
              />
              <span>Potência ativa (W)</span>
            </RadioWrapper>
          </div>
        </RadioInputDiv>
        <InputDiv>
          <span>{isCurrent ? 'Corrente (A)' : 'Potência ativa (W)'}</span>
          <ValidatedInput
            name={`${p}load`}
            type="number"
            value={segment.load}
            onChange={(event) => updateSegment(index, { load: event.target.value })}
            errors={errors}
            touched={touched}
            onBlur={handleBlur}
            disabled={isLoading}
          />
        </InputDiv>
        <InputDiv>
          <span>Fator de potência (opcional)</span>
          <ValidatedInput
            name={`${p}powerFactor`}
            type="number"
            step="any"
            value={segment.powerFactor}
            placeholder={
              isCurrent ? 'Desconhecido: usa o pior caso' : `Padrão ${options?.defaults?.powerFactor ?? 0.8}`
            }
            onChange={(event) => updateSegment(index, { powerFactor: event.target.value })}
            errors={errors}
            touched={touched}
            onBlur={handleBlur}
            disabled={isLoading}
          />
        </InputDiv>
        {form.mode === 'calculate-drop' ? (
          <InputDiv>
            <span>Seção do cabo (mm²)</span>
            <div>
              <Select
                style={{ marginLeft: '0' }}
                value={segment.section}
                onChange={(event) => updateSegment(index, { section: event.target.value })}
                disabled={isLoading}
              >
                <option value="">Selecione</option>
                {(options?.sections || []).map((section) => (
                  <option key={section} value={section}>
                    {fmt(section)}
                  </option>
                ))}
              </Select>
            </div>
          </InputDiv>
        ) : (
          <InputDiv>
            <span>Seção mínima (mm²)</span>
            <ValidatedInput
              name={`${p}minSection`}
              type="number"
              step="any"
              value={segment.minSection}
              placeholder={`Padrão ${options?.defaults?.minSection ?? 2.5}`}
              onChange={(event) => updateSegment(index, { minSection: event.target.value })}
              errors={errors}
              touched={touched}
              onBlur={handleBlur}
              disabled={isLoading}
            />
          </InputDiv>
        )}
        <InputDiv>
          <span>Limite local de queda (%)</span>
          <ValidatedInput
            name={`${p}maxDropPercent`}
            type="number"
            step="any"
            value={segment.maxDropPercent}
            placeholder="Automático — repartir orçamento"
            onChange={(event) => updateSegment(index, { maxDropPercent: event.target.value })}
            errors={errors}
            touched={touched}
            onBlur={handleBlur}
            disabled={isLoading}
          />
        </InputDiv>
      </SegmentBox>
    )
  }

  function renderAdvancedOptions() {
    const adv = form.advanced
    const defaults = options?.defaults || {}
    const advancedFields = [
      ['temperature', 'Temperatura do condutor (°C)', 'Máxima da isolação'],
      ['reactanceOhmPerKm', 'Reatância por condutor (Ω/km)', defaults.reactanceOhmPerKm],
      ['temperatureFactor', 'Fator de temperatura', defaults.temperatureFactor],
      ['groupFactor', 'Fator de agrupamento', defaults.groupFactor],
    ]

    return (
      <>
        <Hint>
          Valem para todos os trechos. Campos em branco usam o padrão conservador da API (cobre/PVC/A2, fatores 0,87 e
          0,80). Os fatores reduzem a capacidade de corrente; não alteram a corrente usada na queda.
        </Hint>
        <InputDiv>
          <span>Isolação</span>
          <div>
            <Select
              style={{ marginLeft: '0' }}
              value={adv.insulation}
              onChange={(event) => updateAdvanced({ insulation: event.target.value })}
              disabled={isLoading}
            >
              <option value="">Padrão ({defaultConductor.insulation})</option>
              {(options?.conductorOptions?.insulations || []).map((insulation) => (
                <option key={insulation} value={insulation}>
                  {insulation}
                </option>
              ))}
            </Select>
          </div>
        </InputDiv>
        <InputDiv>
          <span>Método de instalação</span>
          <div>
            <Select
              style={{ marginLeft: '0' }}
              value={adv.method}
              onChange={(event) => changeMethod(event.target.value)}
              disabled={isLoading}
            >
              <option value="">Padrão ({defaultConductor.method})</option>
              {methods.map((method) => (
                <option key={method} value={method}>
                  {method}
                </option>
              ))}
            </Select>
          </div>
        </InputDiv>
        {arrangements.length > 0 && (
          <InputDiv>
            <span>Disposição dos condutores</span>
            <div>
              <Select
                style={{ marginLeft: '0' }}
                value={adv.arrangement || arrangements[0]}
                onChange={(event) => updateAdvanced({ arrangement: event.target.value })}
                disabled={isLoading}
              >
                {arrangements.map((arrangement) => (
                  <option key={arrangement} value={arrangement}>
                    {ARRANGEMENT_LABELS[arrangement] || arrangement}
                  </option>
                ))}
              </Select>
            </div>
          </InputDiv>
        )}
        {advancedFields.map(([name, label, placeholder]) => (
          <InputDiv key={name}>
            <span>{label}</span>
            <ValidatedInput
              name={name}
              type="number"
              step="any"
              value={adv[name]}
              placeholder={placeholder !== undefined ? `Padrão: ${placeholder}` : ''}
              onChange={(event) => updateAdvanced({ [name]: event.target.value })}
              errors={errors}
              touched={touched}
              onBlur={handleBlur}
              disabled={isLoading}
            />
          </InputDiv>
        ))}
      </>
    )
  }

  function renderSummary() {
    const total = result.total
    const isOk = result.status === 'ok'

    return (
      <>
        <h2>Resultados</h2>
        <p>
          Situação:{' '}
          <StatusTag $ok={isOk}>
            {isOk ? 'Atende' : result.status === 'out-of-range' ? 'Sem cabo disponível' : 'Não atende'}
          </StatusTag>
        </p>
        {total ? (
          <MathDiv>
            <BlockMath
              math={`\\boxed{\\Delta U_{total}= ${tex(total.dropVolts)} \\, \\text{V} \\;(${tex(total.dropPercent)}\\%)}`}
            />
            <BlockMath math={`U_{recebida}= ${tex(total.receivingVoltage)} \\, \\text{V}`} />
          </MathDiv>
        ) : (
          <p>Resultado total indisponível: não há cabo que atenda a todos os critérios em algum trecho.</p>
        )}
        <TableContainer>
          <Table style={{ marginTop: '10px' }}>
            <thead>
              <Tr>
                <Th>Trecho</Th>
                <Th>Cabo</Th>
                <Th>Corrente (A)</Th>
                <Th>Queda local (%)</Th>
                <Th>Queda acumulada (%)</Th>
                <Th>Situação</Th>
              </Tr>
            </thead>
            <tbody>
              {result.segments.map((segment, index) => {
                const ok = segment.wire && segment.withinAmpacity && segment.withinDropLimit

                return (
                  <Tr key={index}>
                    <Td>{segment.name || segment.id || index + 1}</Td>
                    <Td>{segment.wire ? `${fmt(segment.wire.section)} mm²` : 'Indisponível'}</Td>
                    <Td>{fmt(segment.current)}</Td>
                    <Td>{segment.calculation ? fmt(segment.calculation.dropPercent) : '—'}</Td>
                    <Td>{segment.accumulated ? fmt(segment.accumulated.dropPercent) : '—'}</Td>
                    <Td>
                      {!segment.wire ? (
                        <StatusTag $ok={false}>{REASONS[segment.reason] ? 'Sem cabo' : 'Indisponível'}</StatusTag>
                      ) : (
                        <StatusTag $ok={Boolean(ok)}>{ok ? 'Atende' : 'Não atende'}</StatusTag>
                      )}
                    </Td>
                  </Tr>
                )
              })}
            </tbody>
          </Table>
        </TableContainer>
      </>
    )
  }

  function renderBasisNote(segment) {
    if (segment.powerFactorBasis === 'maximum-impedance-drop') {
      return 'FP não informado: usada a queda máxima pela impedância (limite superior para qualquer FP indutivo).'
    }

    if (segment.powerFactorBasis === 'default') return 'FP não informado: adotada a hipótese padrão de 0,80.'

    return null
  }

  function renderSegmentMemory(segment, index) {
    const calc = segment.calculation
    const note = renderBasisNote(segment)

    return (
      <div key={index}>
        <h3>
          Trecho {index + 1}
          {segment.name && segment.name !== String(index + 1) ? ` — ${segment.name}` : ''}
        </h3>
        {!segment.wire || !calc ? (
          <MathDiv>
            <p>{REASONS[segment.reason] || 'Cabo indisponível para este trecho.'}</p>
          </MathDiv>
        ) : (
          <MathDiv>
            <p>
              Cabo: {segment.wire.name || `${fmt(segment.wire.section)} mm²`} — capacidade tabelada{' '}
              {fmt(segment.wire.ampacity)} A, corrigida {fmt(segment.wire.correctedAmpacity)} A (F
              <sub>t</sub> = {fmt(segment.temperatureFactor)}, F<sub>g</sub> = {fmt(segment.groupFactor)}).
            </p>
            <BlockMath
              math={`R= ${tex(calc.resistanceOhm, 5)} \\, \\Omega \\qquad X= ${tex(calc.reactanceOhm, 5)} \\, \\Omega`}
            />
            {note && <p>{note}</p>}
            <BlockMath
              math={`\\Delta U_{R}= ${tex(calc.resistiveDropVolts)} \\, \\text{V} \\qquad \\Delta U_{X}= ${tex(calc.reactiveDropVolts)} \\, \\text{V}`}
            />
            <BlockMath
              math={`\\boxed{\\Delta U= ${tex(calc.dropVolts)} \\, \\text{V} \\;(${tex(calc.dropPercent)}\\%)}`}
            />
            <BlockMath math={`P_{perdas}= ${tex(calc.powerLossWatts)} \\, \\text{W}`} />
            <p>
              Limite local: {fmt(segment.maxDropPercent)}% — {segment.withinDropLimit ? 'atendido' : 'não atendido'}.
              Corrente corrigida {fmt(segment.correctedCurrent)} A — capacidade{' '}
              {segment.withinAmpacity ? 'atendida' : 'não atendida'}.
            </p>
            {segment.accumulated && (
              <BlockMath
                math={`\\Delta U_{acum}= ${tex(segment.accumulated.dropVolts)} \\, \\text{V} \\;(${tex(segment.accumulated.dropPercent)}\\%) \\qquad U_{recebida}= ${tex(segment.accumulated.receivingVoltage)} \\, \\text{V}`}
              />
            )}
          </MathDiv>
        )}
      </div>
    )
  }

  const headerGradient = 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
  const systems = Object.keys(options?.systems || { '127V/220V': 1, '220V/380V': 1 })

  return (
    <PageContainer>
      <PageHeader $gradient={headerGradient}>
        <div className="header-content">
          <BackButton to="/eletrica">
            <FiArrowLeft /> Voltar para Elétrica
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/eletrica">Elétrica</a>
            <span>/</span>
            <span>Queda de Tensão</span>
          </div>
          <h1>⚡ Queda de Tensão</h1>
          <p className="subtitle">
            Dimensione o cabo de cobre ou verifique a queda de tensão em um percurso com um ou mais trechos.
          </p>
          <div className="badges">
            <Badge>NBR 5410</Badge>
            <Badge>Cobre</Badge>
            <Badge>Múltiplos trechos</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)"
        $border="2px solid #fb923c"
        $primaryColor="#c2410c"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta calculadora</h3>
            <p>
              Informe os trechos do percurso, da origem até a carga, com a carga que passa em cada um (incluindo tudo o
              que está a jusante). Escolha entre dimensionar a menor seção comercial ou calcular a queda para um cabo
              informado.
            </p>
            <p>
              Retorna queda local e acumulada, tensão recebida e verificações de capacidade de corrente. Os 3% de
              orçamento são um critério conservador do produto, não um limite normativo universal.
            </p>
            <div className="norms">
              <span>NBR 5410</span>
              <span>Regime permanente</span>
              <span>Trifásico equilibrado</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />
          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <form onSubmit={handleCalculate}>
            <h3>Dados gerais</h3>
            <RadioInputDiv>
              <span>Modo</span>
              <div>
                {MODES.map((mode) => (
                  <RadioWrapper key={mode.value} style={radioStyle}>
                    <input
                      type="radio"
                      name="mode"
                      checked={form.mode === mode.value}
                      onChange={() => updateForm({ mode: mode.value })}
                      disabled={isLoading}
                    />
                    <span>{mode.label}</span>
                  </RadioWrapper>
                ))}
              </div>
            </RadioInputDiv>
            <InputDiv>
              <span>Sistema de tensões</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  value={form.selectedSystem}
                  onChange={(event) => updateForm({ selectedSystem: event.target.value })}
                  disabled={isLoading}
                >
                  {systems.map((system) => (
                    <option key={system} value={system}>
                      {system}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            <InputDiv>
              <span>Alimentação</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  value={form.supplyType}
                  onChange={(event) => changeSupplyType(event.target.value)}
                  disabled={isLoading}
                >
                  {SUPPLY_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            <Hint>
              No trifásico, informe a corrente de linha ou a potência total das três fases. O comprimento é só a
              distância de ida.
            </Hint>
            <InputDiv>
              <span>Queda inicial (%)</span>
              <ValidatedInput
                name="initialDropPercent"
                type="number"
                step="any"
                value={form.initialDropPercent}
                onChange={(event) => updateForm({ initialDropPercent: event.target.value })}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Queda total máxima do percurso (%)</span>
              <ValidatedInput
                name="maxTotalDropPercent"
                type="number"
                step="any"
                value={form.maxTotalDropPercent}
                onChange={(event) => updateForm({ maxTotalDropPercent: event.target.value })}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>

            <h3>Trechos (origem → carga)</h3>
            {form.segments.map((segment, index) => renderSegmentFields(segment, index))}
            <InputDiv>
              <span>Adicionar trecho</span>
              <div>
                <AddButton
                  type="button"
                  onClick={addSegment}
                  disabled={form.segments.length >= MAX_SEGMENTS || isLoading}
                  style={{ marginLeft: 0 }}
                >
                  <FaPlusCircle />
                </AddButton>
              </div>
            </InputDiv>

            <AdvancedOptionsButton type="button" onClick={() => setShowAdvanced(!showAdvanced)} $isOpen={showAdvanced}>
              <span className="arrow-icon">{showAdvanced ? '▼' : '▶'}</span>
              <span className="button-text">{showAdvanced ? 'Ocultar' : 'Mostrar'} Opções Avançadas</span>
            </AdvancedOptionsButton>
            {showAdvanced && renderAdvancedOptions()}

            <ActionButtonsRow $isFormValid={isFormValid}>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate || !options}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>

        {result !== '' && result?.segments && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              {renderSummary()}
            </Result>

            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <p>
                Tensão nominal: {fmt(result.voltage)} V — fator do circuito: {fmt(result.circuitFactor, 4)}. Queda
                inicial: {fmt(result.initialDropPercent)}% — limite total: {fmt(result.maxTotalDropPercent)}%.
              </p>
              {result.segments.map((segment, index) => renderSegmentMemory(segment, index))}
              <p style={{ marginTop: '20px', fontSize: '0.85rem' }}>
                Modelo em regime permanente, sem harmônicas, partida de motores, cabos em paralelo, proteção ou
                curto-circuito. Não constitui projeto elétrico integral nem garantia de conformidade normativa.
              </p>
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
